<!-- eslint-disable vue/custom-event-name-casing -->
<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isPhoneE164, shouldBeUrl } from 'shared/helpers/Validators';

const props = defineProps({
  button: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:button', 'delete:button']);

const { t } = useI18n();

// Track validation state
const validation = reactive({
  type: { isValid: true, message: '' },
  text: { isValid: true, message: '' },
  url: { isValid: true, message: '' },
  phoneNumber: { isValid: true, message: '' },
  example: { isValid: true, message: '' },
});

const isValidText = text => {
  return text.length <= 25;
};

const validateForm = () => {
  let isValid = true;

  // Validate type
  if (!props.button.type) {
    validation.type.isValid = false;
    validation.type.message = t(
      'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.TYPE.REQUIRED'
    );
    isValid = false;
  } else {
    validation.type.isValid = true;
    validation.type.message = '';
  }

  // Validate name based on button type
  if (props.button.type === 'URL') {
    if (!props.button.url) {
      validation.url.isValid = false;
      validation.url.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.URL.REQUIRED'
      );
      isValid = false;
    } else if (!shouldBeUrl(props.button.url)) {
      validation.url.isValid = false;
      validation.url.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.URL.INVALID'
      );
      isValid = false;
    } else {
      validation.url.isValid = true;
      validation.url.message = '';
    }
  } else if (props.button.type === 'PHONE_NUMBER') {
    if (!props.button.phone_number) {
      validation.phoneNumber.isValid = false;
      validation.phoneNumber.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.PHONE_NUMBER.REQUIRED'
      );
      isValid = false;
    } else if (!isPhoneE164(props.button.phone_number)) {
      validation.phoneNumber.isValid = false;
      validation.phoneNumber.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.PHONE_NUMBER.INVALID'
      );
      isValid = false;
    } else {
      validation.phoneNumber.isValid = true;
      validation.phoneNumber.message = '';
    }
  } else if (props.button.type === 'COPY_CODE') {
    if (!props.button.example) {
      validation.example.isValid = false;
      validation.example.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.EXAMPLE.REQUIRED'
      );
      isValid = false;
    } else if (!isValidText(props.button.example)) {
      validation.example.isValid = false;
      validation.example.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.EXAMPLE.INVALID'
      );
      isValid = false;
    } else {
      validation.example.isValid = true;
      validation.example.message = '';
    }
  }

  if (props.button.type !== 'COPY_CODE') {
    if (!props.button.text) {
      validation.text.isValid = false;
      validation.text.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.TEXT.REQUIRED'
      );
      isValid = false;
    } else if (!isValidText(props.button.text)) {
      validation.text.isValid = false;
      validation.text.message = t(
        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.VALIDATION.TEXT.INVALID'
      );
      isValid = false;
    } else {
      validation.text.isValid = true;
      validation.text.message = '';
    }
  }

  return isValid;
};

const handleDelete = () => {
  emit('delete:button');
};

const type = computed({
  get() {
    return props.button.type;
  },
  set(value) {
    emit('update:button', { ...props.button, type: value });
  },
});

const url = computed({
  get() {
    return props.button.url;
  },
  set(value) {
    emit('update:button', { ...props.button, url: value });
  },
});

const phoneNumber = computed({
  get() {
    return props.button.phone_number;
  },
  set(value) {
    emit('update:button', { ...props.button, phone_number: value });
  },
});

const text = computed({
  get() {
    return props.button.text;
  },
  set(value) {
    emit('update:button', { ...props.button, text: value });
  },
});

const example = computed({
  get() {
    return props.button.example;
  },
  set(value) {
    emit('update:button', { ...props.button, example: value });
  },
});

const buttonTypes = [
  {
    key: 'PHONE_NUMBER',
    label: t('WHATSAPP_TEMPLATES.COMPONENTS.BUTTONS_TYPE.PHONE_NUMBER'),
  },
  {
    key: 'URL',
    label: t('WHATSAPP_TEMPLATES.COMPONENTS.BUTTONS_TYPE.URL'),
  },
  // {
  //   key: 'COPY_CODE',
  //   label: t('WHATSAPP_TEMPLATES.COMPONENTS.BUTTONS_TYPE.COPY_CODE'),
  // },
  {
    key: 'QUICK_REPLY',
    label: t('WHATSAPP_TEMPLATES.COMPONENTS.BUTTONS_TYPE.QUICK_REPLY'),
  },
];

watch(
  () => props.button,
  () => {
    validateForm();
  },
  { deep: true }
);
</script>

<template>
  <div class="flex basis-0 w-full gap-2 items-stretch flex-col md:flex-row">
    <label class="w-full">
      {{
        $t(
          'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.TYPE.LABEL'
        )
      }}
      <select v-model="type" class="action__question" required>
        <option
          v-for="(buttonType, index) in buttonTypes"
          :key="index"
          :value="buttonType.key"
        >
          {{ buttonType.label }}
        </option>
      </select>
      <span v-if="!validation.type.isValid" class="text-red-500 text-sm">
        {{ validation.type.message }}
      </span>
    </label>

    <label v-if="button.type !== 'COPY_CODE'" class="w-full">
      {{
        $t(
          'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.TEXT.LABEL'
        )
      }}
      <input
        v-model="text"
        type="text"
        :placeholder="
          $t(
            'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.TEXT.PLACEHOLDER'
          )
        "
        required
      />
      <span v-if="!validation.text.isValid" class="text-red-500 text-sm">
        {{ validation.text.message }}
      </span>
    </label>

    <div v-if="button.type === 'URL'" class="w-full flex gap-2">
      <label class="w-full">
        {{
          $t(
            'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.URL.LABEL'
          )
        }}
        <input
          v-model="url"
          type="text"
          :placeholder="
            $t(
              'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.URL.PLACEHOLDER'
            )
          "
          required
        />
        <span v-if="!validation.url.isValid" class="text-red-500 text-sm">
          {{ validation.url.message }}
        </span>
      </label>
    </div>

    <div v-if="button.type === 'PHONE_NUMBER'" class="w-full">
      <label class="w-full">
        {{
          $t(
            'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.PHONE_NUMBER.LABEL'
          )
        }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="
            $t(
              'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.PHONE_NUMBER.PLACEHOLDER'
            )
          "
          required
        />
        <span
          v-if="!validation.phoneNumber.isValid"
          class="text-red-500 text-sm"
        >
          {{ validation.phoneNumber.message }}
        </span>
      </label>
    </div>

    <div v-if="button.type === 'COPY_CODE'" class="w-full">
      <label class="w-full">
        {{
          $t(
            'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.EXAMPLE.LABEL'
          )
        }}
        <input
          v-model="example"
          type="text"
          :placeholder="
            $t(
              'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.BUTTON_ITEM.FIELDS.EXAMPLE.PLACEHOLDER'
            )
          "
          required
        />
        <span v-if="!validation.example.isValid" class="text-red-500 text-sm">
          {{ validation.example.message }}
        </span>
      </label>
    </div>
    <div class="flex items-center justify-center">
      <woot-button
        variant="smooth"
        color-scheme="alert"
        size="small"
        class-names="grey-btn"
        class="min-w-8"
        icon="dismiss-circle"
        type="button"
        @click="handleDelete"
      />
    </div>
  </div>
</template>
